/**
 * downscaleImage — client-side auto-resize so proof-of-delivery photos
 * (or any user-uploaded image) stay under a soft byte budget without
 * hitting the network.
 *
 * Strategy:
 *   1. Draw the source Image / File into a canvas capped at `maxLongestEdge`.
 *   2. Encode as JPEG at 0.85 quality first.
 *   3. If still over `maxBytes`, drop quality in 0.1 steps down to 0.4.
 *   4. If still too big, shrink `maxLongestEdge` by 20 % and retry.
 *   5. Returns a new File (preserving the original name with `-resized.jpg`).
 *
 * Bandwidth on shaky mobile networks is the real bottleneck for drivers, so
 * we default to 5 MB even though the backend accepts bigger.
 */
export async function downscaleImage(file, opts = {}) {
  const {
    maxBytes = 5 * 1024 * 1024,     // 5 MB target
    maxLongestEdge = 2000,          // long side capped in px
    minLongestEdge = 640,           // never shrink below this — POD readable
    initialQuality = 0.85,
    minQuality = 0.4,
    mime = 'image/jpeg',
  } = opts;

  // Fast path: tiny files pass straight through.
  if (file.size <= maxBytes && (file.type === 'image/jpeg' || file.type === 'image/png')) {
    return file;
  }

  const img = await _loadImage(file);
  let edge = Math.min(maxLongestEdge, Math.max(img.width, img.height));
  let quality = initialQuality;
  let attempts = 0;
  let out = null;

  while (attempts < 12) {
    const blob = await _encode(img, edge, quality, mime);
    if (blob.size <= maxBytes) {
      out = blob;
      break;
    }
    // Try lowering quality first (cheaper), then edge, then repeat.
    if (quality > minQuality + 0.001) {
      quality = Math.max(minQuality, quality - 0.1);
    } else {
      edge = Math.max(minLongestEdge, Math.floor(edge * 0.8));
      quality = initialQuality;
      if (edge === minLongestEdge && quality === minQuality) {
        // Best effort — accept the last blob.
        out = blob;
        break;
      }
    }
    attempts += 1;
  }
  if (!out) out = await _encode(img, minLongestEdge, minQuality, mime);

  const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([out], `${base}-resized.jpg`, { type: mime });
}


function _loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function _encode(img, longestEdge, quality, mime) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ratio = Math.min(1, longestEdge / Math.max(img.width, img.height));
    canvas.width = Math.max(1, Math.round(img.width * ratio));
    canvas.height = Math.max(1, Math.round(img.height * ratio));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

export default downscaleImage;
